import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../common/api';

export interface Study {
  id: string;
  description: string;
  inclusionCriteria: string[];
  exclusionCriteria: string[];
  usefulLinks?: { name: string; url: string }[];
}

export interface StudyState {
  studies: Study[];
  loading: boolean;
  error: string | null;
}

const initialState: StudyState = {
  studies: [],
  loading: false,
  error: null,
};

const messageFromError = (error: any) => {
  if (error.response) {
    // Server responded with a status other than 200 range
    return error.response.data.message;
  } else if (error.request) {
    // Request was made but no response received
    return 'No response from server';
  } else {
    // Something else happened while setting up the request
    return error.message;
  }
};

export const fetchStudies = createAsyncThunk<Study[], void, { rejectValue: string }>('study/fetchStudies', async (_, thunkAPI) => {
  try {
    const response = await api.get('/api/v1/studies');
    return response.data;
  } catch (error) {
    return thunkAPI.rejectWithValue(`Failed to load studies - ${messageFromError(error)}`);
  }
});

export const createStudy = createAsyncThunk<Study, Study, { rejectValue: string }>('study/createStudy', async (studyData, thunkAPI) => {
  try {
    const response = await api.post('/api/v1/studies', {
      id: studyData.id,
      description: studyData.description,
      inclusionCriteria: studyData.inclusionCriteria,
      exclusionCriteria: studyData.exclusionCriteria,
      usefulLinks: studyData.usefulLinks,
    });
    thunkAPI.dispatch(fetchStudies());
    return response.data;
  } catch (error: any) {
    console.log(error);
    return thunkAPI.rejectWithValue(`Failed to create study - ${messageFromError(error)}`);
  }
});

export const updateStudy = createAsyncThunk<Study, Study, { rejectValue: string }>('study/updateStudy', async (studyData, thunkAPI) => {
  try {
    const response = await api.patch(`/api/v1/studies/${studyData.id}`, {
      id: studyData.id,
      description: studyData.description,
      inclusionCriteria: studyData.inclusionCriteria,
      exclusionCriteria: studyData.exclusionCriteria,
      usefulLinks: studyData.usefulLinks,
    });
    thunkAPI.dispatch(fetchStudies());
    return response.data;
  } catch (error: any) {
    console.log(error);
    return thunkAPI.rejectWithValue(`Failed to update study - ${messageFromError(error)}`);
  }
});

export const deleteStudy = createAsyncThunk<string, string, { rejectValue: string }>('study/deleteStudy', async (id, thunkAPI) => {
  try {
    await api.delete(`/api/v1/studies/${id}`);
    return id;
  } catch (error: any) {
    console.log(error);
    return thunkAPI.rejectWithValue(`Failed to delete study - ${messageFromError(error)}`);
  }
});

const studySlice = createSlice({
  name: 'study',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStudies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudies.fulfilled, (state, action: PayloadAction<Study[]>) => {
        state.studies = action.payload;
        state.loading = false;
      })
      .addCase(fetchStudies.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load studies';
      })
      .addCase(createStudy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStudy.fulfilled, (state, action: PayloadAction<Study>) => {
        state.studies.push(action.payload);
        state.loading = false;
      })
      .addCase(createStudy.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create study';
      })
      .addCase(updateStudy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStudy.fulfilled, (state, action: PayloadAction<Study>) => {
        const index = state.studies.findIndex((study) => study.id === action.payload.id);
        if (index !== -1) {
          state.studies[index] = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateStudy.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update study';
      })
      .addCase(deleteStudy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStudy.fulfilled, (state, action: PayloadAction<string>) => {
        state.studies = state.studies.filter((study) => study.id !== action.payload);
        state.loading = false;
      })
      .addCase(deleteStudy.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete study';
      });
  },
});

export default studySlice.reducer;
